import { queryClient } from '@/router'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { API_ENDPOINTS } from '@/lib/constants/api'
import { useDialog } from '@/lib/hooks/use-dialog'
import { getPricingQuery } from '@/lib/queries/business/pricing/queries'
import { getApplicantDetailQuery } from '@/lib/queries/business/v1/applicant/queries'
import { getCategoryListByApplicantQuery } from '@/lib/queries/business/v1/case/path/queries'
import { searchApplicantQueryKey } from '@/lib/queries/business/v2/applicant/search/queries'
import { getUserQuery } from '@/lib/queries/business/v2/user/queries'

export const useApplicantCasesLayout = () => {
  const { applicantId } = useParams({
    from: '/_dashboard/applicants/$applicantId',
  })
  const navigate = useNavigate()

  const [currentCategoryTab, setCurrentCategoryTab] = useState(applicantId)

  const { data: dataApplicant } = useQuery(getApplicantDetailQuery(applicantId))
  const { data: dataListCategory } = useQuery(getCategoryListByApplicantQuery({ applicantId, containsData: true }))
  const { data: dataUser } = useQuery(getUserQuery)
  const { data: dataPricing } = useQuery(getPricingQuery)

  const {
    isDialogOpen: isRegisterCaseDialogOpen,
    closeDialog: closeRegisterCaseDialog,
    openDialog: openRegisterCaseDialog,
  } = useDialog()
  const {
    isDialogOpen: isCategoryDeleteDialogOpen,
    openDialog: openCategoryDeleteDialog,
    closeDialog: closeCategoryDeleteDialog,
  } = useDialog()

  /**
   * Default maximum number of cases allowed for the user's current plan.
   */
  const defaultMaxCases = useMemo(() => {
    const xs = dataPricing?.map(({ max_cases }) => max_cases).filter((x) => !!x)
    if (!xs) {
      return 0
    }
    return Math.min(...xs)
  }, [dataPricing])

  const handleInvalidate = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries(getApplicantDetailQuery(applicantId)),
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.business.v1.case.path.index],
      }),
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.business.v4.case.search.index],
      }),
      queryClient.invalidateQueries({
        queryKey: searchApplicantQueryKey.base,
      }),
    ])
  }, [applicantId])

  const handleChangeCategoryTab = useCallback(
    (val: string) => {
      setCurrentCategoryTab(val)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [applicantId]
  )

  const handleAddCaseClick = useCallback(() => {
    if (!dataUser || !defaultMaxCases) {
      return
    }

    const numMaxCases = !dataUser.subscription ? defaultMaxCases : dataUser.extraProductInfo.maxCases

    if (dataUser.extraProductInfo.currentCases >= numMaxCases) {
      toast.warning(
        "You've reached the maximum number of cases for your current plan. Please upgrade to add more cases.",
        {
          duration: 10000,
          action: {
            label: 'Upgrade now',
            onClick: () => {
              void navigate({ to: '/settings' })
            },
          },
        }
      )
      return
    }

    openRegisterCaseDialog()
  }, [dataUser, defaultMaxCases, openRegisterCaseDialog, navigate])

  // update the default tab to the selected applicant's cases
  useEffect(() => {
    setCurrentCategoryTab(applicantId)
  }, [applicantId])

  return {
    currentCategoryTab,
    applicantId,
    dataApplicant,
    dataListCategory,
    isRegisterCaseDialogOpen,
    isCategoryDeleteDialogOpen,
    openCategoryDeleteDialog,
    openRegisterCaseDialog,
    closeRegisterCaseDialog,
    closeCategoryDeleteDialog,
    handleInvalidate,
    handleChangeCategoryTab,
    handleAddCaseClick,
  }
}
